import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  Calendar,
  Camera,
  Edit3,
  FileCheck,
  FileText,
  Hourglass,
  Link as LinkIcon,
  Printer,
  QrCode,
  Save,
  Search,
  Trash2,
  Upload,
  Utensils,
} from "lucide-react";

function pad2(n) {
  return String(n).padStart(2, "0");
}

function toIsoLocalMinute(d) {
  const dt = new Date(d);
  dt.setMinutes(dt.getMinutes() - dt.getTimezoneOffset());
  return dt.toISOString().slice(0, 16);
}

function toYmd(d) {
  const dt = new Date(d);
  return `${dt.getFullYear()}-${pad2(dt.getMonth() + 1)}-${pad2(
    dt.getDate()
  )}`;
}

function fmtPreviewDateTime(isoOrEmpty) {
  if (!isoOrEmpty) return "--/--/--";
  const d = new Date(isoOrEmpty);
  if (Number.isNaN(d.getTime())) return "--/--/--";
  return (
    d.toLocaleDateString("it-IT") +
    " " +
    d.toLocaleTimeString("it-IT", { hour: "2-digit", minute: "2-digit" })
  );
}

function fmtPreviewDate(ymdOrEmpty) {
  if (!ymdOrEmpty) return "--/--/--";
  const d = new Date(ymdOrEmpty + "T00:00:00");
  if (Number.isNaN(d.getTime())) return "--/--/--";
  return d.toLocaleDateString("it-IT");
}

function makePreviewItems(files) {
  return files.map((file) => ({
    id: `${file.name}-${file.size}-${Date.now()}-${Math.random()}`,
    file,
    preview: URL.createObjectURL(file),
  }));
}

const DEFAULT_LABEL_FORMAT = "80x50";

export default function StampaEtichette() {
  const [preparazione, setPreparazione] = useState("");
  const [dataProduzione, setDataProduzione] = useState(() =>
    toIsoLocalMinute(new Date())
  );

  const [scadenza, setScadenza] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() + 3);
    return toYmd(d);
  });

  const [note, setNote] = useState("");

  const [ddtList, setDdtList] = useState([]);
  const [ddtSearch, setDdtSearch] = useState("");
  const [selectedDdt, setSelectedDdt] = useState([]);

  const [loadingDdt, setLoadingDdt] = useState(true);
  const [saving, setSaving] = useState(false);

  const [prodottiSalvati, setProdottiSalvati] = useState([]);
  const [loadingProdotti, setLoadingProdotti] = useState(false);
  const [savingProdotto, setSavingProdotto] = useState(false);

  const [fotoMateriePrime, setFotoMateriePrime] = useState([]);

  const [result, setResult] = useState(null);
  const [error, setError] = useState("");

  const [usaMateriaPrima, setUsaMateriaPrima] = useState(true);
  const [usaDdt, setUsaDdt] = useState(true);

  const inputCameraRef = useRef(null);
  const inputFileRef = useRef(null);

  useEffect(() => {
    setLoadingDdt(true);
    setError("");

    fetch("/api/etichette_get_ddt.php", { credentials: "include" })
      .then((r) => r.json())
      .then((data) => {
        if (Array.isArray(data)) {
          setDdtList(data);
        } else {
          setError(data?.error || "Errore nel caricamento dei DDT");
        }
      })
      .catch(() => setError("Errore di rete nel caricamento dei DDT"))
      .finally(() => setLoadingDdt(false));
  }, []);

  useEffect(() => {
    loadProdottiSalvati();
  }, []);

  useEffect(() => {
    return () => {
      fotoMateriePrime.forEach((item) => {
        if (item.preview) URL.revokeObjectURL(item.preview);
      });
    };
  }, [fotoMateriePrime]);

  async function loadProdottiSalvati() {
    try {
      setLoadingProdotti(true);

      const res = await fetch("/api/etichette_prodotti_list.php", {
        credentials: "include",
      });
      const data = await res.json();

      if (data?.ok) {
        setProdottiSalvati(Array.isArray(data.items) ? data.items : []);
      }
    } catch (err) {
      console.error("Errore caricamento prodotti:", err);
    } finally {
      setLoadingProdotti(false);
    }
  }

  const toggleDdt = (id) => {
    setSelectedDdt((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const filteredDdt = useMemo(() => {
    const s = ddtSearch.trim().toLowerCase();
    if (!s) return ddtList;

    return ddtList.filter((d) => {
      const hay = [d.ddt, d.fornitore, d.data_iso || d.data, String(d.id)]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();

      return hay.includes(s);
    });
  }, [ddtList, ddtSearch]);

  const selectedCount = selectedDdt.length;

  async function handleSaveProdotto() {
    const nome = preparazione.trim();

    if (!nome) {
      setError("Inserisci prima il nome della preparazione / prodotto.");
      return;
    }

    try {
      setSavingProdotto(true);
      setError("");

      const res = await fetch("/api/etichette_prodotto_save.php", {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ nome }),
      });

      const data = await res.json();

      if (!data?.ok) {
        throw new Error(data?.error || "Errore nel salvataggio del prodotto");
      }

      await loadProdottiSalvati();
    } catch (err) {
      console.error(err);
      setError(err.message || "Errore nel salvataggio del prodotto");
    } finally {
      setSavingProdotto(false);
    }
  }

  function handleAddFoto(fileList) {
    const files = Array.from(fileList || []).filter((f) =>
      f.type.startsWith("image/")
    );
    if (!files.length) return;

    const items = makePreviewItems(files);
    setFotoMateriePrime((prev) => [...prev, ...items]);
  }

  function handleRemoveFoto(id) {
    setFotoMateriePrime((prev) => {
      const found = prev.find((x) => x.id === id);
      if (found?.preview) URL.revokeObjectURL(found.preview);
      return prev.filter((x) => x.id !== id);
    });
  }

  function buildPrintPageUrl(etichettaId) {
    if (!etichettaId) return null;
    return `/api/etichette_pdf.php?id=${etichettaId}&format=${DEFAULT_LABEL_FORMAT}&download=1&_=${Date.now()}`;
  }

  function buildImageUrl(etichettaId) {
    if (!etichettaId) return null;
    return `/api/etichette_image.php?id=${etichettaId}&format=${DEFAULT_LABEL_FORMAT}&download=1&_=${Date.now()}`;
  }

  function openPrintPage(etichettaId) {
    const url = buildPrintPageUrl(etichettaId);
    if (!url) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = url;
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }

  function openImagePage(etichettaId) {
    const url = buildImageUrl(etichettaId);
    if (!url) return;

    const isMobile = /Android|iPhone|iPad|iPod/i.test(navigator.userAgent);

    if (isMobile) {
      window.location.href = url;
      return;
    }

    window.open(url, "_blank", "noopener,noreferrer");
  }

  async function handleSubmit(e) {
    e?.preventDefault?.();
    setError("");
    setResult(null);

    if (!usaMateriaPrima && !usaDdt) {
      setError(
        "Seleziona almeno una modalità: etichetta materia prima, DDT, oppure entrambe."
      );
      return;
    }

    if (usaMateriaPrima) {
      if (!preparazione.trim()) {
        setError("Inserisci la preparazione / prodotto.");
        return;
      }

      if (!dataProduzione) {
        setError("Inserisci la data di produzione.");
        return;
      }
    }

    if (usaDdt) {
      if (selectedDdt.length === 0) {
        setError("Seleziona almeno un DDT.");
        return;
      }
    }

    try {
      setSaving(true);

      const formData = new FormData();
      formData.append("usa_materia_prima", usaMateriaPrima ? "1" : "0");
      formData.append("usa_ddt", usaDdt ? "1" : "0");

      formData.append("preparazione", preparazione.trim());
      formData.append("data_produzione", dataProduzione);
      formData.append("note", note);
      formData.append("scadenza_ui", scadenza);
      formData.append("formato", DEFAULT_LABEL_FORMAT);

      if (usaDdt) {
        selectedDdt.forEach((id) => {
          formData.append("ddt_ids[]", String(id));
        });
      }

      if (usaMateriaPrima) {
        fotoMateriePrime.forEach((item) => {
          formData.append("foto_materie_prime[]", item.file);
        });
      }

      const res = await fetch("/api/etichette_genera.php", {
        method: "POST",
        credentials: "include",
        body: formData,
      });

      const data = await res.json();

      if (data?.status === "ok") {
        setResult(data);

        fotoMateriePrime.forEach((item) => {
          if (item.preview) URL.revokeObjectURL(item.preview);
        });
        setFotoMateriePrime([]);

        if (data?.etichetta_id) {
          openPrintPage(data.etichetta_id);
        }
      } else {
        setError(data?.error || "Errore nella generazione etichetta");
      }
    } catch (err) {
      console.error(err);
      setError("Errore di rete nella generazione etichetta");
    } finally {
      setSaving(false);
    }
  }

  const previewName = usaMateriaPrima
    ? (preparazione || "PRODOTTO").toUpperCase()
    : "TRACCIABILITÀ DDT";

  const previewNotes = usaMateriaPrima
    ? note
    : "Etichetta collegata ai documenti di trasporto selezionati.";

  const previewProd = usaMateriaPrima
    ? fmtPreviewDateTime(dataProduzione)
    : "--/--/--";

  const previewExp = usaMateriaPrima ? fmtPreviewDate(scadenza) : "--/--/--";

  const printPageUrl = result?.etichetta_id
    ? buildPrintPageUrl(result.etichetta_id)
    : null;
  const imageUrl = result?.etichetta_id
    ? buildImageUrl(result.etichetta_id)
    : null;

  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
      <section className="lg:col-span-7 space-y-6">
        <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden">
          <h3 className="text-xl font-bold text-gray-800 mb-6 flex items-center gap-3">
            <div className="w-10 h-10 rounded-full bg-[#003B71]/10 text-[#003B71] flex items-center justify-center shrink-0">
              <Edit3 className="w-5 h-5" />
            </div>
            Dati Produzione
          </h3>

          {error && (
            <div className="mb-4 rounded-[18px] border border-red-100 bg-red-50/60 px-4 py-3 text-sm text-red-700">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-2">
              <label className="flex items-start gap-3 p-4 rounded-[18px] border border-gray-200 bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={usaMateriaPrima}
                  onChange={(e) => setUsaMateriaPrima(e.target.checked)}
                  className="mt-1 w-4 h-4"
                />
                <div>
                  <div className="font-bold text-gray-800">
                    Etichetta materia prima
                  </div>
                  <div className="text-sm text-gray-500">
                    Usa preparazione, data, scadenza, note e foto.
                  </div>
                </div>
              </label>

              <label className="flex items-start gap-3 p-4 rounded-[18px] border border-gray-200 bg-gray-50 cursor-pointer">
                <input
                  type="checkbox"
                  checked={usaDdt}
                  onChange={(e) => setUsaDdt(e.target.checked)}
                  className="mt-1 w-4 h-4"
                />
                <div>
                  <div className="font-bold text-gray-800">Collega DDT</div>
                  <div className="text-sm text-gray-500">
                    Associa uno o più DDT alla tracciabilità.
                  </div>
                </div>
              </label>
            </div>

            {usaMateriaPrima && (
              <>
                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                    Preparazione / Prodotto
                  </label>

                  <div className="flex flex-col md:flex-row gap-3">
                    <div className="relative flex-1">
                      <Utensils className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="text"
                        list="etichette-prodotti-list"
                        value={preparazione}
                        onChange={(e) => setPreparazione(e.target.value)}
                        placeholder="Es: Lasagne al ragù..."
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] focus:bg-white transition-all shadow-sm placeholder:font-light"
                      />

                      <datalist id="etichette-prodotti-list">
                        {prodottiSalvati.map((item) => (
                          <option key={item.id || item.nome} value={item.nome} />
                        ))}
                      </datalist>
                    </div>

                    <button
                      type="button"
                      onClick={handleSaveProdotto}
                      disabled={savingProdotto}
                      className="shrink-0 inline-flex items-center justify-center gap-2 px-5 py-4 rounded-[20px] bg-[#003B71] hover:bg-[#002B5B] text-white font-bold shadow-sm transition-all disabled:opacity-60"
                    >
                      <Save className="w-4 h-4" />
                      {savingProdotto ? "Salvataggio..." : "Salva"}
                    </button>
                  </div>

                  <div className="mt-2 text-xs text-gray-500">
                    {loadingProdotti
                      ? "Caricamento prodotti salvati..."
                      : "Puoi scrivere il nome del prodotto oppure selezionarlo dai suggerimenti."}
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                      Data Produzione (con ora)
                    </label>
                    <div className="relative">
                      <Calendar className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="datetime-local"
                        value={dataProduzione}
                        onChange={(e) => setDataProduzione(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] focus:bg-white transition-all shadow-sm"
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                      Scadenza (UI)
                    </label>
                    <div className="relative">
                      <Hourglass className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 w-5 h-5" />
                      <input
                        type="date"
                        value={scadenza}
                        onChange={(e) => setScadenza(e.target.value)}
                        className="w-full pl-12 pr-4 py-4 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] focus:bg-white transition-all shadow-sm"
                      />
                    </div>
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-bold text-gray-500 uppercase tracking-wider mb-2 ml-1">
                    Note Aggiuntive / Lotto
                  </label>
                  <textarea
                    rows={3}
                    value={note}
                    onChange={(e) => setNote(e.target.value)}
                    placeholder="Ingredienti, allergeni o note lotto..."
                    className="w-full px-5 py-4 bg-gray-50 border border-gray-200 rounded-[20px] text-gray-800 font-medium focus:outline-none focus:ring-2 focus:ring-[#003B71] focus:bg-white transition-all shadow-sm resize-none placeholder:font-light"
                  />
                </div>
              </>
            )}

            <div className="lg:hidden">
              <button
                type="submit"
                disabled={saving}
                className="w-full bg-[#003B71] hover:bg-[#002B5B] text-white font-bold text-lg py-4 rounded-[20px] shadow-lg hover:shadow-xl transition-all flex items-center justify-center gap-3 disabled:opacity-60"
              >
                <Printer className="w-6 h-6" />
                {saving ? "Generazione..." : "Genera e Scarica"}
              </button>
            </div>
          </form>
        </div>

        {usaDdt && (
          <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="flex justify-between items-center mb-6">
              <h3 className="text-xl font-bold text-gray-800 flex items-center gap-3">
                <div className="w-10 h-10 rounded-full bg-[#FA8216]/10 text-[#FA8216] flex items-center justify-center shrink-0">
                  <LinkIcon className="w-5 h-5" />
                </div>
                Tracciabilità Materie Prime
              </h3>

              <div className="relative w-48 hidden md:block">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
                <input
                  type="text"
                  value={ddtSearch}
                  onChange={(e) => setDdtSearch(e.target.value)}
                  placeholder="Cerca DDT..."
                  className="w-full pl-9 pr-3 py-2 bg-gray-50 rounded-[14px] text-sm border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FA8216]"
                />
              </div>
            </div>

            <p className="text-sm text-gray-400 font-light mb-4">
              Seleziona i Documenti di Trasporto (DDT) delle materie prime
              utilizzate.
            </p>

            <div className="relative w-full md:hidden mb-4">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                value={ddtSearch}
                onChange={(e) => setDdtSearch(e.target.value)}
                placeholder="Cerca DDT..."
                className="w-full pl-9 pr-3 py-3 bg-gray-50 rounded-[16px] text-sm border border-gray-200 focus:outline-none focus:ring-1 focus:ring-[#FA8216]"
              />
            </div>

            {loadingDdt ? (
              <div className="text-sm text-gray-500">Caricamento DDT...</div>
            ) : filteredDdt.length === 0 ? (
              <div className="text-sm text-gray-500">
                Nessun DDT trovato.
                <br />
                Vai prima in “Ricevimento merci” e registra un nuovo DDT.
              </div>
            ) : (
              <div className="space-y-3 max-h-72 overflow-y-auto pr-1">
                {filteredDdt.map((d) => {
                  const checked = selectedDdt.includes(Number(d.id));
                  const dateLabel = (d.data_iso || d.data || "").toString();

                  return (
                    <label
                      key={d.id}
                      className={[
                        "flex items-center gap-4 p-4 rounded-[18px] border bg-white transition-all cursor-pointer group",
                        checked
                          ? "border-[#FA8216] bg-[#FFF7ED]"
                          : "border-gray-100 hover:border-[#FA8216]/50 hover:bg-[#FFF7ED]",
                      ].join(" ")}
                    >
                      <input
                        type="checkbox"
                        className="w-5 h-5 rounded-md border-gray-300 text-[#FA8216] focus:ring-[#FA8216]"
                        checked={checked}
                        onChange={() => toggleDdt(Number(d.id))}
                      />

                      <div className="flex-1 min-w-0">
                        <div className="flex justify-between items-start gap-3">
                          <span
                            className={[
                              "font-bold truncate",
                              checked ? "text-[#003B71]" : "text-gray-800",
                            ].join(" ")}
                          >
                            {d.ddt || `DDT #${d.id}`}
                          </span>

                          <span
                            className={[
                              "text-xs font-mono shrink-0",
                              checked
                                ? "text-[#FA8216] font-bold"
                                : "text-gray-400",
                            ].join(" ")}
                          >
                            {dateLabel}
                          </span>
                        </div>

                        <div className="text-sm text-gray-600">
                          Fornitore:{" "}
                          <span
                            className={[
                              "font-medium",
                              checked ? "text-[#003B71]" : "text-gray-700",
                            ].join(" ")}
                          >
                            {d.fornitore || "—"}
                          </span>
                        </div>
                      </div>

                      <div className="p-2 text-gray-400 group-hover:text-[#003B71] transition-colors">
                        {d.has_ddt_file ? (
                          checked ? (
                            <FileCheck className="w-4 h-4" />
                          ) : (
                            <FileText className="w-4 h-4" />
                          )
                        ) : (
                          <FileText className="w-4 h-4 opacity-40" />
                        )}
                      </div>
                    </label>
                  );
                })}
              </div>
            )}

            <div className="mt-4 text-xs text-gray-500">
              Selezionati: <b>{selectedCount}</b>
            </div>
          </div>
        )}

        {usaMateriaPrima && (
          <div className="bg-white rounded-[24px] p-6 md:p-8 shadow-sm border border-gray-100 relative overflow-hidden">
            <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
              <div>
                <h4 className="text-base font-bold text-gray-800">
                  Foto etichette materie prime
                </h4>
                <p className="text-sm text-gray-500 mt-1">
                  Scatta o carica le foto delle etichette dei prodotti
                  utilizzati nella preparazione.
                </p>
              </div>

              <div className="flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={() => inputCameraRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-[14px] bg-[#FA8216] hover:bg-[#e97510] text-white px-4 py-2.5 text-sm font-bold transition-all"
                >
                  <Camera className="w-4 h-4" />
                  Scatta foto
                </button>

                <button
                  type="button"
                  onClick={() => inputFileRef.current?.click()}
                  className="inline-flex items-center gap-2 rounded-[14px] border border-gray-200 bg-white hover:bg-gray-50 text-gray-700 px-4 py-2.5 text-sm font-bold transition-all"
                >
                  <Upload className="w-4 h-4" />
                  Carica immagini
                </button>
              </div>
            </div>

            <input
              ref={inputCameraRef}
              type="file"
              accept="image/*"
              capture="environment"
              className="hidden"
              onChange={(e) => {
                handleAddFoto(e.target.files);
                e.target.value = "";
              }}
            />

            <input
              ref={inputFileRef}
              type="file"
              accept="image/*"
              multiple
              className="hidden"
              onChange={(e) => {
                handleAddFoto(e.target.files);
                e.target.value = "";
              }}
            />

            {fotoMateriePrime.length > 0 ? (
              <div className="mt-4 grid grid-cols-2 md:grid-cols-3 xl:grid-cols-4 gap-4">
                {fotoMateriePrime.map((img) => (
                  <div
                    key={img.id}
                    className="relative rounded-[18px] overflow-hidden border border-gray-200 bg-white shadow-sm"
                  >
                    <img
                      src={img.preview}
                      alt="Etichetta materia prima"
                      className="w-full h-40 object-cover"
                    />

                    <button
                      type="button"
                      onClick={() => handleRemoveFoto(img.id)}
                      className="absolute top-2 right-2 inline-flex items-center justify-center rounded-full bg-white/95 border border-gray-200 p-2 text-gray-700 hover:bg-red-50 hover:text-red-600 transition-all"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            ) : (
              <div className="mt-4 rounded-[18px] border border-dashed border-gray-300 bg-gray-50 px-4 py-8 text-center text-sm text-gray-500">
                Nessuna foto caricata.
              </div>
            )}
          </div>
        )}
      </section>

      <section className="lg:col-span-5 space-y-6">
        <div className="lg:sticky lg:top-24">
          <h3 className="text-gray-400 font-medium text-sm uppercase tracking-wider mb-3 ml-1">
            Anteprima di Stampa
          </h3>

          <div className="bg-white rounded-[24px] p-6 shadow-lg border border-gray-200 relative overflow-hidden">
            <div
              className="absolute inset-0 opacity-[0.35]"
              style={{
                backgroundImage:
                  "radial-gradient(#cbd5e1 1px, transparent 1px)",
                backgroundSize: "10px 10px",
              }}
            />
            <div className="relative mx-auto max-w-[560px]">
              <div className="aspect-[8/5] border-2 border-black bg-white p-3 md:p-4 flex flex-col">
                <div className="flex items-start justify-between gap-3 border-b-2 border-black pb-2">
                  <div className="min-w-0 flex-1">
                    <div className="text-[10px] md:text-[11px] font-bold uppercase tracking-[0.18em]">
                      {usaMateriaPrima ? "Prodotto Interno" : "Tracciabilità"}
                    </div>
                    <div className="mt-1 font-mono text-[11px] md:text-xs truncate">
                      {result?.lotto ? `Lotto: ${result.lotto}` : "Lotto: --"}
                    </div>
                  </div>

                  <div className="w-14 h-14 md:w-16 md:h-16 shrink-0 border-2 border-black p-1 flex items-center justify-center">
                    <QrCode className="w-full h-full text-black" />
                  </div>
                </div>

                <div className="grid grid-cols-[1fr_auto] gap-3 pt-2">
                  <div className="min-w-0">
                    <h2
                      className="text-lg md:text-[22px] font-black uppercase leading-none break-words"
                      style={{ fontFamily: "'Share Tech Mono', monospace" }}
                    >
                      {previewName}
                    </h2>
                  </div>
                  <div className="hidden md:flex items-start">
                    <div className="px-2 py-1 border border-black text-[10px] font-bold uppercase">
                      80 x 50 mm
                    </div>
                  </div>
                </div>

                <div className="mt-2 flex-1 border border-dashed border-gray-400 px-2.5 py-2 overflow-hidden">
                  <p
                    className="text-[10px] md:text-[11px] text-gray-700 leading-tight break-words"
                    style={{ fontFamily: "'Share Tech Mono', monospace" }}
                  >
                    {previewNotes}
                  </p>
                </div>

                <div className="mt-2 grid grid-cols-2 gap-3 border-t-2 border-black pt-2">
                  <div className="min-w-0">
                    <span className="block text-[9px] md:text-[10px] uppercase font-bold">
                      Produzione
                    </span>
                    <span
                      className="block text-[12px] md:text-[14px] font-bold leading-tight"
                      style={{ fontFamily: "'Share Tech Mono', monospace" }}
                    >
                      {previewProd}
                    </span>
                  </div>
                  <div className="min-w-0 pt-2 md:pt-3">
                    <span className="block text-[9px] md:text-[10px] uppercase font-bold">
                      Scadenza
                    </span>
                    <span
                      className="block text-[12px] md:text-[14px] font-bold leading-tight"
                      style={{ fontFamily: "'Share Tech Mono', monospace" }}
                    >
                      {previewExp}
                    </span>
                  </div>
                </div>

                <div className="mt-2 pt-2 border-t border-dashed border-gray-400 flex items-center justify-between gap-3">
                  <div className="min-w-0">
                    <span className="block text-[9px] md:text-[10px]">
                      Operatore: <b>JD</b>
                    </span>
                    <div className="h-5 md:h-6 mt-1 w-24 md:w-28 bg-black" />
                  </div>
                  <div className="w-9 h-9 md:w-10 md:h-10 border-2 border-black rounded-full flex items-center justify-center font-bold text-[10px]">
                    OK
                  </div>
                </div>

                <div
                  className="text-[9px] md:text-[10px] text-center text-gray-800 mt-2 pt-1 border-t border-gray-200 uppercase tracking-[0.18em]"
                  style={{ fontFamily: "'Share Tech Mono', monospace" }}
                >
                  Norma HACCP
                </div>
              </div>
            </div>
          </div>

          <button
            type="button"
            disabled={saving}
            onClick={handleSubmit}
            className="hidden lg:flex w-full mt-6 bg-[#003B71] hover:bg-[#002B5B] text-white font-bold text-lg py-4 rounded-[20px] shadow-lg hover:shadow-xl transition-all transform hover:-translate-y-1 items-center justify-center gap-3 disabled:opacity-60"
          >
            <Printer className="w-6 h-6" />
            {saving ? "Generazione..." : "Genera e Scarica"}
          </button>

          {result && (
            <div className="mt-6 bg-white rounded-[20px] border border-gray-100 shadow-sm p-5">
              <div className="text-sm text-gray-500 mb-1">
                Etichetta generata
              </div>

              <div className="font-mono text-xs bg-gray-100 inline-block px-2 py-1 rounded-md text-gray-700">
                Lotto: {result.lotto}
              </div>

              <div className="mt-4 flex flex-wrap gap-2">
                {printPageUrl && (
                  <button
                    type="button"
                    onClick={() => openPrintPage(result.etichetta_id)}
                    className="inline-flex items-center gap-2 rounded-[12px] bg-[#003B71] text-white px-3 py-2 text-sm font-bold hover:bg-[#002B5B] transition-all"
                  >
                    <Printer className="w-4 h-4" />
                    PDF
                  </button>
                )}

                {imageUrl && (
                  <button
                    type="button"
                    onClick={() => openImagePage(result.etichetta_id)}
                    className="inline-flex items-center gap-2 rounded-[12px] bg-[#EEF2FF] text-[#003B71] px-3 py-2 text-sm font-bold hover:bg-[#003B71] hover:text-white transition-all"
                  >
                    <Camera className="w-4 h-4" />
                    PNG
                  </button>
                )}

                {result.visualizza_ddt_url && (
                  <a
                    href={result.visualizza_ddt_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-[12px] bg-[#EEF2FF] text-[#003B71] px-3 py-2 text-sm font-bold hover:bg-[#003B71] hover:text-white transition-all"
                  >
                    <LinkIcon className="w-4 h-4" />
                    DDT
                  </a>
                )}

                {result.qr_url && (
                  <a
                    href={result.qr_url}
                    target="_blank"
                    rel="noreferrer"
                    className="inline-flex items-center gap-2 rounded-[12px] border border-gray-200 text-gray-600 px-3 py-2 text-sm font-bold hover:border-gray-400 hover:text-gray-900 transition-all"
                  >
                    <QrCode className="w-4 h-4" />
                    QR
                  </a>
                )}
              </div>

              {Array.isArray(result.foto_materie_prime) &&
                result.foto_materie_prime.length > 0 && (
                  <div className="mt-4 text-xs text-green-700 bg-green-50 border border-green-100 rounded-xl px-3 py-2">
                    Foto materie prime salvate:{" "}
                    <b>{result.foto_materie_prime.length}</b>
                  </div>
                )}

              {Array.isArray(result.foto_errors) &&
                result.foto_errors.length > 0 && (
                  <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-100 rounded-xl px-3 py-2">
                    Alcune foto non sono state salvate correttamente.
                  </div>
                )}
            </div>
          )}
        </div>
      </section>
    </div>
  );
}
